import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Scelta, Spunta } from '../../componenti/base.tsx'
import { CampiAbitativa, CampiFormazione, CampiServizi, CampoSede, giornoLeggibile, useRegole } from '../SchedaSezioni.tsx'
import {
  corpoAbitativa, corpoFormazione, corpoServizi, corpoVuoto, giornoIso, nessunErrore, oggiLocale, statoDaScheda,
  verificaAbitativa, verificaFormazione, verificaServizi, type CataloghiRegole, type Errori, type ModoNascita,
  type SchedaLetta,
} from '../../servizi/regole-anagrafica.ts'
import {
  DATI_MANCANTI, TESTO_SOLO_CONSULTAZIONE, datoInFrase, mancantiDaMostrare, riepilogoMancanti, soloConsultazione,
  titoloDato,
} from '../../servizi/dati-mancanti.ts'

/**
 * «Completa la scheda» (indicazioni CIDIS del 17/10/2026, esteso il 19/10/2026 con la 460).
 *
 * Che cosa manca lo dice l'API (`dati_mancanti`, dalla funzione core.dati_mancanti del database):
 * la stessa definizione dello stato della scheda, della pagina «Da completare» e dei contatori. Qui
 * i codici diventano passi, un dato o una sezione alla volta: prima gli obbligatori, poi i
 * consigliati, che si saltano e sono segnati come tali. Consensi e firme di terzi si sistemano nella
 * loro sezione: il passo porta li'. La categoria di utenti non e' piu' un dato da completare (474): si
 * chiede nell'intervento, dove serve. Il modulo firmato dei progetti (495) si stampa e si carica dal
 * riquadro «Moduli dei progetti»: anche li' porta il passo.
 *
 * Si salva un passo alla volta: chi si ferma a meta' non perde quello che ha gia' scritto.
 */

type TipoPasso =
  | 'nome' | 'sede' | 'codice_fiscale' | 'nascita' | 'fascia' | 'italiano' | 'appartenenza'
  | 'nascita_da_verificare' | 'titolo' | 'formazione' | 'servizi' | 'abitativa'
  | 'consensi' | 'firme_terzi' | 'modulo_progetto'

type Passo = { tipo: TipoPasso; codici: string[]; obbligatorio: boolean; titolo: string; perche: string }

/** Sezioni della scheda dove si sistemano i dati che non si inseriscono da qui. */
export type Sezione = 'consensi' | 'scheda-anagrafici' | 'moduli-progetto'

const COLLEGAMENTI: Partial<Record<TipoPasso, { sezione: Sezione; comando: string; testo: string }>> = {
  consensi: {
    sezione: 'consensi', comando: 'Vai ai consensi',
    testo: 'I consensi si registrano dal modulo firmato, nella sezione Consensi della scheda.',
  },
  modulo_progetto: {
    sezione: 'moduli-progetto', comando: 'Vai ai moduli dei progetti',
    testo: 'Nel riquadro «Moduli dei progetti» il modulo del progetto si stampa, anche insieme al modulo CIDIS, e si '
      + 'carica firmato.',
  },
  firme_terzi: {
    sezione: 'consensi', comando: 'Vai alle firme da completare',
    testo: 'Nella sezione Consensi la firma da completare ha il suo percorso guidato: si carica il documento che prova il potere di firma.',
  },
  appartenenza: {
    sezione: 'scheda-anagrafici', comando: 'Vai ai dati anagrafici',
    testo: 'L’ente o la sede di appartenenza si indicano nei dati anagrafici, alla voce Ente.',
  },
}

const passo = (tipo: TipoPasso, codici: string[], obbligatorio: boolean, titolo?: string): Passo => ({
  tipo, codici, obbligatorio,
  titolo: titolo ?? titoloDato(codici[0]),
  perche: DATI_MANCANTI[codici[0]]?.perche ?? '',
})

/** I passi del percorso, dai codici che l'API dice mancanti. Vuoto se chi guarda non modifica la scheda. */
export function passiDaCompletare(s: SchedaLetta): Passo[] {
  if (s.mascherata || !s.puo.modificare || !s.dati_mancanti) return []
  const o = new Set(s.dati_mancanti.obbligatori)
  const c = new Set(s.dati_mancanti.consigliati)
  const passi: Passo[] = []
  const nomi = ['cognome', 'nome'].filter((x) => o.has(x))
  if (nomi.length) passi.push(passo('nome', nomi, true, 'Cognome e nome'))
  if (o.has('sede_riferimento')) passi.push(passo('sede', ['sede_riferimento'], true))
  if (o.has('codice_fiscale')) passi.push(passo('codice_fiscale', ['codice_fiscale'], true))
  // Senza nascita il passo chiede anche la fascia, se «non nota».
  if (o.has('nascita')) passi.push(passo('nascita', ['nascita', ...(o.has('fascia_eta') ? ['fascia_eta'] : [])], true))
  else if (o.has('fascia_eta')) passi.push(passo('fascia', ['fascia_eta'], true))
  if (o.has('livello_italiano')) passi.push(passo('italiano', ['livello_italiano'], true))
  if (o.has('appartenenza')) passi.push(passo('appartenenza', ['appartenenza'], true))

  if (c.has('nascita_da_verificare')) passi.push(passo('nascita_da_verificare', ['nascita_da_verificare'], false))
  if (c.has('riconoscimento_titolo')) passi.push(passo('titolo', ['riconoscimento_titolo'], false))
  if (c.has('formazione_lavoro')) {
    passi.push(passo('formazione', ['formazione_lavoro', ...(c.has('servizi_bancari') ? ['servizi_bancari'] : [])], false,
      c.has('servizi_bancari') ? 'Formazione, lavoro e servizi bancari' : undefined))
  } else if (c.has('servizi_bancari')) passi.push(passo('servizi', ['servizi_bancari'], false))
  if (c.has('condizione_abitativa')) passi.push(passo('abitativa', ['condizione_abitativa'], false))
  if (c.has('consensi')) passi.push(passo('consensi', ['consensi'], false))
  if (c.has('firme_terzi')) passi.push(passo('firme_terzi', ['firme_terzi'], false))
  if (c.has('modulo_progetto')) passi.push(passo('modulo_progetto', ['modulo_progetto'], false))
  return passi
}

/** Il passo e' ancora da fare sulla scheda di adesso? */
const ancoraDaFare = (p: Passo, s: SchedaLetta) =>
  p.codici.some((codice) => s.dati_mancanti?.obbligatori.includes(codice) || s.dati_mancanti?.consigliati.includes(codice))

const RICONOSCIMENTI = [
  { valore: 'si', etichetta: 'Sì' },
  { valore: 'no', etichetta: 'No' },
  { valore: 'in_corso', etichetta: 'In corso di riconoscimento' },
]

const MODI_NASCITA = [
  { valore: 'data', etichetta: 'La data completa' },
  { valore: 'anno', etichetta: 'Solo l’anno' },
  { valore: 'ignota', etichetta: 'Non è nota' },
]

/** Senza nascita la fascia d'eta' si chiede nello stesso passo: si conta e si elenca una volta sola. */
const daMostrare = (s: SchedaLetta) => mancantiDaMostrare(s.dati_mancanti)

const elenco = (codici: string[]) => codici.map(datoInFrase).join(', ')
const minuscola = (testo: string) => testo.charAt(0).toLowerCase() + testo.slice(1)

export function CompletaScheda({ scheda, ricarica, apriSubito = false, richiesta = 0, vaiAllaSezione }: {
  scheda: SchedaLetta
  ricarica: () => Promise<void> | void
  /** Apre il percorso al primo disegno: arrivando da «Da completare». */
  apriSubito?: boolean
  /** Cresce a ogni «Modifica la scheda» dell'intestazione con dati obbligatori mancanti: apre il percorso. */
  richiesta?: number
  vaiAllaSezione: (sezione: Sezione) => void
}) {
  const [aperto, setAperto] = useState(false)
  const passi = passiDaCompletare(scheda)
  const mancanti = daMostrare(scheda)
  const numero = mancanti.obbligatori.length + mancanti.consigliati.length

  useEffect(() => {
    if (apriSubito && passi.length) setAperto(true)
    // Solo all'arrivo sulla scheda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apriSubito])

  useEffect(() => {
    if (richiesta > 0) setAperto(true)
  }, [richiesta])

  // Chi non modifica la scheda lo legge qui, con il perche' (Osservazioni 4 del 23/10/2026): la
  // Direzione non capiva perche' le schede di certe sedi non si modificassero. Quanti dati mancano
  // lo sa, con obbligatori e consigliati detti per nome; non li inserisce.
  if (soloConsultazione(scheda)) {
    const riepilogo = riepilogoMancanti(scheda.dati_mancanti)
    return (
      <Avviso tipo="informazione">
        {TESTO_SOLO_CONSULTAZIONE}
        {riepilogo && <p>{riepilogo}</p>}
      </Avviso>
    )
  }

  if (!numero && !aperto) return null

  const o = mancanti.obbligatori.length
  return (
    <>
      {numero > 0 && (
        <Avviso tipo={o ? 'attenzione' : 'informazione'}>
          {o > 0
            ? (
              <>
                <strong>{o === 1 ? 'Manca un dato obbligatorio' : `Mancano ${o} dati obbligatori`}</strong>: finché
                {o === 1 ? ' manca' : ' mancano'} la scheda resta da completare.
                <ul className="elenco-avviso">
                  {mancanti.obbligatori.map((c) => <li key={c}>{titoloDato(c)}: {minuscola(DATI_MANCANTI[c]?.perche ?? '')}</li>)}
                </ul>
              </>
            )
            : <><strong>La scheda ha tutti i dati obbligatori.</strong>{' '}</>}
          {mancanti.consigliati.length > 0 && (
            <p>
              {o > 0 ? 'Consigliati, si possono saltare: ' : 'Si consiglia di aggiungere: '}
              {elenco(mancanti.consigliati)}.
            </p>
          )}
          <div className="riga">
            <Bottone variante={o ? 'primario' : 'secondario'} dimensione="piccolo" onClick={() => setAperto(true)}>
              {o ? 'Completa la scheda' : 'Aggiungili adesso'}
            </Bottone>
          </div>
        </Avviso>
      )}
      {aperto && (
        <Percorso scheda={scheda} passi={passi} ricarica={ricarica} chiudi={() => setAperto(false)}
                  vaiAllaSezione={(sezione) => { setAperto(false); vaiAllaSezione(sezione) }} />
      )}
    </>
  )
}

function Percorso({ scheda, passi: iniziali, ricarica, chiudi, vaiAllaSezione }: {
  scheda: SchedaLetta; passi: Passo[]; ricarica: () => Promise<void> | void; chiudi: () => void
  vaiAllaSezione: (sezione: Sezione) => void
}) {
  // I passi si fissano all'apertura: la scheda si ricarica dopo ogni salvataggio, e l'elenco non
  // deve cambiare sotto gli occhi di chi lo sta percorrendo. Un passo gia' sistemato da un altro
  // (i servizi bancari insieme alla formazione) si salta da solo.
  const [passi] = useState(iniziali)
  const [indice, setIndice] = useState(0)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const { opzioni, ha, cat } = useRegole()
  const iniziale = useMemo(() => statoDaScheda(scheda), [scheda])
  const oggi = oggiLocale()

  // Valori dei passi. Partono dalla scheda letta all'apertura.
  const [cognome, setCognome] = useState(scheda.cognome ?? '')
  const [nome, setNome] = useState(scheda.nome ?? '')
  const [sede, setSede] = useState(scheda.sede_riferimento ?? '')
  const [italiano, setItaliano] = useState('')
  const [haCf, setHaCf] = useState(false)
  const [cf, setCf] = useState('')
  const [motivo, setMotivo] = useState('')
  const [specifica, setSpecifica] = useState('')
  const [modo, setModo] = useState<ModoNascita | ''>('')
  const [data, setData] = useState('')
  const [anno, setAnno] = useState('')
  const [fascia, setFascia] = useState('')
  const [dataGiusta, setDataGiusta] = useState<'' | 'si' | 'no'>('')
  const [controllata, setControllata] = useState(false)
  const [riconoscimento, setRiconoscimento] = useState('')
  const [formazione, setFormazione] = useState(iniziale.formazione)
  const [servizi, setServizi] = useState(iniziale.servizi)
  const [abitativa, setAbitativa] = useState(iniziale.abitativa)
  const [errori, setErrori] = useState<Errori>({})
  const [erroriServizi, setErroriServizi] = useState<Errori>({})

  const attuale = passi[indice]
  const finito = indice >= passi.length

  // Salta i passi che nel frattempo non mancano piu'.
  useEffect(() => {
    if (attuale && !inCorso && !ancoraDaFare(attuale, scheda)) setIndice((i) => i + 1)
  }, [attuale, scheda, inCorso])

  const avanti = () => { setErrore(null); setErrori({}); setErroriServizi({}); setIndice((i) => i + 1) }

  async function salva() {
    if (!attuale) return
    setErrore(null)
    const cataloghi = cat as CataloghiRegole
    const patch = (corpo: Record<string, unknown>) => api.patch(`/persone/${scheda.id}`, corpo)
    // Verifiche dei moduli di sezione prima di inviare.
    if (attuale.tipo === 'formazione' || attuale.tipo === 'servizi') {
      const e = attuale.tipo === 'formazione' ? verificaFormazione(formazione, cataloghi) : {}
      const es = verificaServizi(servizi, cataloghi)
      if (!nessunErrore(e) || !nessunErrore(es)) {
        setErrori(e); setErroriServizi(es); setErrore('Correggere i campi segnalati.'); return
      }
    }
    if (attuale.tipo === 'abitativa') {
      const e = verificaAbitativa(abitativa, cataloghi)
      if (!nessunErrore(e)) { setErrori(e); setErrore('Correggere i campi segnalati.'); return }
    }
    setInCorso(true)
    try {
      switch (attuale.tipo) {
        case 'nome':
          await patch({ cognome: cognome.trim(), nome: nome.trim() }); break
        case 'sede':
          await patch({ sede_riferimento: sede }); break
        case 'codice_fiscale':
          await patch(haCf
            ? { cf: cf.replace(/\s+/g, '').toUpperCase() }
            : { motivo_assenza_cf_codice: motivo, motivo_assenza_cf: ha('motivo_assenza_cf', motivo, 'richiede_specifica') ? specifica.trim() : null })
          break
        case 'nascita':
          await patch(modo === 'data'
            ? { data_nascita: data, precisione_nascita: 'esatta', anno_nascita: null, fascia_eta_manuale: null }
            : modo === 'anno'
              ? { data_nascita: null, precisione_nascita: 'solo_anno', anno_nascita: Number(anno), fascia_eta_manuale: null }
              : { data_nascita: null, precisione_nascita: 'ignota', anno_nascita: null, fascia_eta_manuale: fascia || null })
          break
        case 'fascia':
          await patch({ fascia_eta_manuale: fascia }); break
        case 'italiano':
          await patch({ livello_italiano: italiano }); break
        case 'nascita_da_verificare':
          // La data giusta si conferma, quella sbagliata si corregge: in tutti e due i casi la
          // precisione diventa esatta, perche' chi salva l'ha controllata su un documento.
          await patch({ data_nascita: dataGiusta === 'si' ? giornoIso(scheda.data_nascita) : data, precisione_nascita: 'esatta' })
          break
        case 'titolo':
          // Il riconoscimento sta nella rilevazione lavorativa: se ne registra una nuova uguale
          // all'ultima, con la risposta in piu', come fa il modulo Formazione – Lavoro.
          await api.post(`/persone/${scheda.id}/situazioni/lavorativa`,
            { ...corpoFormazione(iniziale.formazione), riconoscimento_titolo: riconoscimento })
          break
        case 'formazione': {
          const lavoro = corpoFormazione(formazione)
          if (!corpoVuoto(lavoro)) await api.post(`/persone/${scheda.id}/situazioni/lavorativa`, lavoro)
          if (servizi.voci.length) await api.put(`/persone/${scheda.id}/servizi-bancari`, corpoServizi(servizi, cataloghi))
          break
        }
        case 'servizi':
          await api.put(`/persone/${scheda.id}/servizi-bancari`, corpoServizi(servizi, cataloghi)); break
        case 'abitativa':
          await api.post(`/persone/${scheda.id}/situazioni/abitativa`, corpoAbitativa(abitativa)); break
        default:
          break
      }
      await ricarica()
      avanti()
    } catch (e) {
      setErrore(e instanceof ErroreApi
        ? (e.stato === 404 && attuale.tipo === 'sede' ? 'La sede scelta non rientra nei tuoi incarichi.' : e.message)
        : 'Non è andata a buon fine: controlla la connessione e riprova.')
      if (e instanceof ErroreApi) await Promise.resolve(ricarica()).catch(() => undefined)
    } finally {
      setInCorso(false)
    }
  }

  if (finito) return <Esito scheda={scheda} chiudi={chiudi} />

  const collegamento = COLLEGAMENTI[attuale.tipo]
  const pronto = (() => {
    switch (attuale.tipo) {
      case 'nome': return !!cognome.trim() && !!nome.trim()
      case 'sede': return !!sede
      case 'codice_fiscale':
        return haCf ? cf.trim().length >= 11
          : !!motivo && (!ha('motivo_assenza_cf', motivo, 'richiede_specifica') || !!specifica.trim())
      case 'nascita':
        return modo === 'data' ? !!data && data <= oggi
          : modo === 'anno' ? /^\d{4}$/.test(anno) && Number(anno) >= 1900 && Number(anno) <= Number(oggi.slice(0, 4))
          : modo === 'ignota' ? !!fascia || !attuale.codici.includes('fascia_eta')
          : false
      case 'fascia': return !!fascia
      case 'italiano': return !!italiano
      case 'nascita_da_verificare':
        return controllata && (dataGiusta === 'si' || (dataGiusta === 'no' && !!data && data <= oggi))
      case 'titolo': return !!riconoscimento
      case 'formazione': return !corpoVuoto(corpoFormazione(formazione)) || servizi.voci.length > 0
      case 'servizi': return servizi.voci.length > 0
      case 'abitativa': return !corpoVuoto(corpoAbitativa(abitativa))
      default: return false
    }
  })()
  const ultimo = indice + 1 >= passi.length
  const larga = attuale.tipo === 'formazione' || attuale.tipo === 'abitativa' || attuale.tipo === 'servizi'

  return (
    <Modale titolo={`Completa la scheda · ${indice + 1} di ${passi.length}`} chiudi={chiudi} larga={larga}
            azioni={<>
              <Bottone variante="discreto" onClick={avanti}>{attuale.obbligatorio ? 'Salta per ora' : 'Salta'}</Bottone>
              {collegamento
                ? <Bottone variante="primario" onClick={() => vaiAllaSezione(collegamento.sezione)}>{collegamento.comando}</Bottone>
                : (
                  <Bottone variante="primario" inCorso={inCorso} disabled={!pronto} onClick={() => void salva()}>
                    {ultimo ? 'Salva' : 'Salva e vai avanti'}
                  </Bottone>
                )}
            </>}>
      <div className="riga">
        <h3 className="rete-titoletto">{attuale.titolo}</h3>
        {attuale.obbligatorio
          ? <Etichetta tono="attenzione">Obbligatorio</Etichetta>
          : <Etichetta tono="informativo">Consigliato</Etichetta>}
      </div>
      <p className="nota-modulo">
        {attuale.perche}
        {!attuale.obbligatorio && ' Si può saltare: la scheda resta completa anche senza.'}
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {collegamento && <p>{collegamento.testo}</p>}

      {attuale.tipo === 'nome' && (
        <div className="griglia-modulo">
          <Campo etichetta="Cognome" obbligatorio valore={cognome} maxLength={100} cambia={setCognome} autoComplete="off" />
          <Campo etichetta="Nome" obbligatorio valore={nome} maxLength={100} cambia={setNome} autoComplete="off" />
        </div>
      )}

      {attuale.tipo === 'sede' && <CampoSede valore={sede} cambia={setSede} obbligatorio />}

      {attuale.tipo === 'codice_fiscale' && (
        <>
          <Scelta etichetta="Adesso" valore={haCf ? 'si' : 'no'} cambia={(v) => setHaCf(v === 'si')}
                  opzioni={[{ valore: 'no', etichetta: 'Non ha ancora il codice fiscale' },
                            { valore: 'si', etichetta: 'Ha il codice fiscale e lo inserisco' }]} />
          {haCf
            ? <div className="campo-maiuscolo">
                <Campo etichetta="Codice fiscale" obbligatorio valore={cf} maxLength={30} cambia={setCf} autoComplete="off"
                       aiuto="Anche il codice numerico provvisorio, che è di 11 cifre." />
              </div>
            : <>
                <Scelta etichetta="Perché non ce l’ha" obbligatorio vuota="— scegli —" valore={motivo}
                        opzioni={opzioni('motivo_assenza_cf')} cambia={setMotivo} />
                {ha('motivo_assenza_cf', motivo, 'richiede_specifica') && (
                  <Campo etichetta="In attesa di che cosa" obbligatorio valore={specifica} maxLength={200} cambia={setSpecifica} />
                )}
              </>}
        </>
      )}

      {attuale.tipo === 'nascita' && (
        <div className="griglia-modulo">
          <Scelta etichetta="Della nascita si conosce" obbligatorio vuota="— scegli —" valore={modo}
                  opzioni={MODI_NASCITA} cambia={(v) => setModo(v as ModoNascita)} />
          {modo === 'data' && (
            <Campo tipo="date" etichetta="Data di nascita" obbligatorio valore={data} max={oggi} cambia={setData} />
          )}
          {modo === 'anno' && (
            <Campo tipo="number" etichetta="Anno di nascita" obbligatorio valore={anno} min={1900}
                   max={Number(oggi.slice(0, 4))} passi={1} modalitaTastiera="numeric" cambia={setAnno} />
          )}
          {modo === 'ignota' && (
            <SceltaFascia valore={fascia} cambia={setFascia} obbligatorio={attuale.codici.includes('fascia_eta')} />
          )}
        </div>
      )}

      {attuale.tipo === 'fascia' && <SceltaFascia valore={fascia} cambia={setFascia} obbligatorio />}

      {attuale.tipo === 'italiano' && (
        <Scelta etichetta="Conoscenza della lingua italiana" obbligatorio vuota="— scegli —" valore={italiano}
                opzioni={opzioni('livello_italiano')} cambia={setItaliano} />
      )}

      {attuale.tipo === 'nascita_da_verificare' && (
        <>
          <p>Data registrata: <strong>{giornoLeggibile(scheda.data_nascita) ?? '—'}</strong></p>
          <Scelta etichetta="Sul documento la data è" obbligatorio vuota="— scegli —" valore={dataGiusta}
                  cambia={(v) => setDataGiusta(v as '' | 'si' | 'no')}
                  opzioni={[{ valore: 'si', etichetta: 'La stessa: è giusta' }, { valore: 'no', etichetta: 'Diversa: la correggo' }]} />
          {dataGiusta === 'no' && (
            <Campo tipo="date" etichetta="Data di nascita corretta" obbligatorio valore={data} max={oggi} cambia={setData} />
          )}
          {dataGiusta && (
            <Spunta valore={controllata} cambia={setControllata} etichetta="Ho controllato la data su un documento" />
          )}
        </>
      )}

      {attuale.tipo === 'titolo' && (
        <Scelta etichetta="Il titolo è riconosciuto in Italia?" obbligatorio vuota="— scegli —" valore={riconoscimento}
                opzioni={RICONOSCIMENTI} cambia={setRiconoscimento} />
      )}

      {attuale.tipo === 'formazione' && (
        <>
          <p className="nota-modulo">Si registra una rilevazione con la data di oggi. Si compila quello che si sa.</p>
          <CampiFormazione stato={formazione} cambia={(p) => setFormazione((s) => ({ ...s, ...p }))} errori={errori}
                           servizi={servizi} cambiaServizi={(p) => setServizi((s) => ({ ...s, ...p }))}
                           erroriServizi={erroriServizi} />
        </>
      )}

      {attuale.tipo === 'servizi' && (
        <CampiServizi servizi={servizi} cambiaServizi={(p) => setServizi((s) => ({ ...s, ...p }))} erroriServizi={erroriServizi} />
      )}

      {attuale.tipo === 'abitativa' && (
        <>
          <p className="nota-modulo">Si registra una rilevazione con la data di oggi. Si compila quello che si sa.</p>
          <CampiAbitativa stato={abitativa} cambia={(p) => setAbitativa((s) => ({ ...s, ...p }))} errori={errori} />
        </>
      )}
    </Modale>
  )
}

function SceltaFascia({ valore, cambia, obbligatorio }: { valore: string; cambia: (v: string) => void; obbligatorio: boolean }) {
  const { opzioni } = useRegole()
  return (
    <Scelta etichetta="Fascia d’età" obbligatorio={obbligatorio} vuota="— scegli —" valore={valore}
            opzioni={opzioni('fascia_eta')} cambia={cambia}
            aiuto="Senza data di nascita si sceglie a mano: serve per chiudere gli interventi." />
  )
}

/** Fine del percorso: che cosa manca ancora, dalla scheda ricaricata. */
function Esito({ scheda, chiudi }: { scheda: SchedaLetta; chiudi: () => void }) {
  const mancanti = daMostrare(scheda)
  const o = mancanti.obbligatori.length
  const c = mancanti.consigliati.length
  let testo: ReactNode
  if (!o && !c) testo = <Avviso tipo="successo">Fatto: la scheda è completa.</Avviso>
  else if (!o) {
    testo = (
      <Avviso tipo="successo">
        La scheda ha tutti i dati obbligatori. {c === 1 ? 'Resta un dato consigliato' : `Restano ${c} dati consigliati`}:
        si possono aggiungere quando si vuole.
      </Avviso>
    )
  } else {
    testo = (
      <Avviso tipo="informazione">
        {o === 1 ? 'Resta un dato obbligatorio' : `Restano ${o} dati obbligatori`}: la scheda resta da completare e
        continua a ricordarlo finché non si inseriscono.
      </Avviso>
    )
  }
  return (
    <Modale titolo="Completa la scheda" chiudi={chiudi}
            azioni={<Bottone variante="primario" onClick={chiudi}>Chiudi</Bottone>}>
      {testo}
    </Modale>
  )
}
